import { Component, EventEmitter, forwardRef, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormControl, ValidatorFn } from '@angular/forms';

export interface EmailChip {
  value: string;
  display: string;
  default?: boolean;
}

@Component({
    selector: 'app-email-chips',
    templateUrl: './email-chips.component.html',
    styleUrls: ['./email-chips.component.scss'],
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => EmailChipsComponent), multi: true }],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EmailChipsComponent implements ControlValueAccessor {

  @Input() placeholder = '';
  @Input() secondaryPlaceholder = '';
  @Input() validators: ValidatorFn[] = [];
  @Input() errorMessages: { [error: string]: string } = {};
  @Input() addOnBlur = false;
  @Output() onAdd = new EventEmitter<EmailChip>();
  @Output() onRemove = new EventEmitter<EmailChip>();

  chips: EmailChip[] = [];
  text = '';
  errors: string[] = [];
  disabled = false;

  private onChange: (value: EmailChip[]) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: EmailChip[]) {
    this.chips = value ?? [];
  }

  registerOnChange(fn: (value: EmailChip[]) => void) {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  onEnter(event: Event) {
    event.preventDefault();
    this.add();
  }

  onBlur() {
    this.onTouched();
    if (this.addOnBlur) {
      this.add();
    }
  }

  onBackspace() {
    if (!this.text && this.chips.length > 0) {
      this.remove(this.chips.length - 1);
    }
  }

  remove(index: number) {
    const [removed] = this.chips.splice(index, 1);
    this.onChange(this.chips);
    this.onRemove.emit(removed);
  }

  private add() {
    const value = this.text.trim();
    if (!value) {
      this.errors = [];
      return;
    }
    const control = new UntypedFormControl(value, this.validators);
    if (control.errors) {
      this.errors = Object.keys(control.errors).map(key => this.errorMessages[key]).filter(message => !!message);
      return;
    }
    this.errors = [];
    this.text = '';
    if (this.chips.some(chip => chip.value === value)) {
      return;
    }
    this.chips.push({ value, display: value });
    this.onChange(this.chips);
    this.onAdd.emit(this.chips[this.chips.length - 1]);
  }
}
